/**
 * API admin : messages reçus des étudiants depuis le portail.
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

export interface MessageEtudiant {
  id: number;
  matricule: string;
  nom_complet: string;
  classe: string;
  niveau: string;
  photo_url?: string | null;
  sujet: string;
  message: string;
  lu: boolean;
  traite: boolean;
  reponse: string;
  date_reponse: string | null;
  repondu_par_nom: string;
  date_creation: string;
  date_lecture: string | null;
  est_important?: boolean;
}

export interface MessagesEtudiantsResponse {
  results: MessageEtudiant[];
  total: number;
  nb_non_lus: number;
  nb_non_traites: number;
  nb_importants?: number;
}

export const messagesEtudiantsApi = createApi({
  reducerPath: 'messagesEtudiantsApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['MessagesEtudiants'],
  endpoints: (builder) => ({
    getMessagesEtudiants: builder.query<MessagesEtudiantsResponse, {
      statut?: 'all' | 'non_lus' | 'non_traites' | 'traites' | 'importants';
      classe?: string;
      niveau?: string;
      q?: string;
      limit?: number;
    } | void>({
      query: (params) => ({
        url: '/portail/admin/messages-etudiants/',
        params: params ?? undefined,
      }),
      providesTags: ['MessagesEtudiants'],
    }),

    marquerMessageEtudiantLu: builder.mutation<{ success: boolean }, number>({
      query: (id) => ({
        url: `/portail/admin/messages-etudiants/${id}/marquer-lu/`,
        method: 'POST',
      }),
      invalidatesTags: ['MessagesEtudiants'],
    }),

    repondreMessageEtudiant: builder.mutation<
      { success: boolean; type?: string },
      { id: number; reponse: string; fichier?: File; duree_audio_sec?: number }
    >({
      query: ({ id, reponse, fichier, duree_audio_sec }) => {
        if (fichier) {
          const fd = new FormData();
          fd.append('reponse', reponse);
          fd.append('fichier', fichier);
          if (duree_audio_sec) fd.append('duree_audio_sec', String(duree_audio_sec));
          return {
            url: `/portail/admin/messages-etudiants/${id}/repondre/`,
            method: 'POST',
            body: fd,
          };
        }
        return {
          url: `/portail/admin/messages-etudiants/${id}/repondre/`,
          method: 'POST',
          body: { reponse },
        };
      },
      invalidatesTags: ['MessagesEtudiants'],
    }),

    archiverMessageEtudiant: builder.mutation<{ success: boolean }, { id: number; traite?: boolean }>({
      query: ({ id, traite = true }) => ({
        url: `/portail/admin/messages-etudiants/${id}/archiver/`,
        method: 'POST',
        body: { traite },
      }),
      invalidatesTags: ['MessagesEtudiants'],
    }),

    toggleImportantMessageEtudiant: builder.mutation<
      { success: boolean; est_important: boolean },
      { id: number; est_important?: boolean }
    >({
      query: ({ id, est_important }) => ({
        url: `/portail/admin/messages-etudiants/${id}/toggle-important/`,
        method: 'POST',
        body: est_important !== undefined ? { est_important } : {},
      }),
      invalidatesTags: ['MessagesEtudiants'],
    }),

    pushTest: builder.mutation<
      { success: boolean; matricule: string; nb_subscriptions_actives: number; nb_pushs_envoyees: number },
      { matricule: string; titre?: string; message?: string }
    >({
      query: (body) => ({
        url: '/portail/admin/push-test/',
        method: 'POST',
        body,
      }),
    }),

    getMessageEtudiantThread: builder.query<{
      thread: {
        id: number;
        matricule: string;
        nom_complet: string;
        classe: string;
        niveau: string;
        option?: string;
        photo_url?: string | null;
        moyenne_courante?: number | null;
        moyenne_label?: string;
        sujet: string;
        date_creation: string;
        derniere_activite: string | null;
        traite: boolean;
      };
      messages: Array<{
        id: string | number;
        expediteur: 'etudiant' | 'scolarite';
        expediteur_nom: string;
        contenu: string;
        type?: 'texte' | 'image' | 'audio' | 'fichier';
        fichier_url?: string | null;
        fichier_nom_original?: string;
        duree_audio_sec?: number;
        date_creation: string;
        lu_par_destinataire?: boolean;
        date_lecture?: string | null;
        supprime?: boolean;
        supprime_par?: string;
        peut_supprimer?: boolean;
      }>;
      paiements?: Array<{
        periode: string;
        periode_label: string;
        est_a_jour: boolean;
        solde: string;
        frais_attendus: string;
        frais_payes: string;
        saisie_manuelle: boolean;
        /** période exigée pour l'accès aux résultats */
        requise?: boolean;
        /** période requise sans aucune ligne de paiement enregistrée */
        absente?: boolean;
      }>;
      acces?: {
        autorise: boolean;
        mode: string;
        solde_total: string;
        nb_a_jour: number;
        nb_present: number;
        nb_periodes_requises: number;
        contrat_type?: string;
        contrat_remarque?: string;
        periodes_requises?: string[];
        periodes_manquantes?: string[];
      };
    }, number>({
      query: (id) => `/portail/admin/messages-etudiants/${id}/`,
      providesTags: (result, err, id) => [{ type: 'MessagesEtudiants', id }],
    }),

    supprimerPostMessageEtudiant: builder.mutation<{ success: boolean }, number>({
      query: (postId) => ({
        url: `/portail/admin/messages-etudiants/posts/${postId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['MessagesEtudiants'],
    }),
  }),
});

export const {
  useGetMessagesEtudiantsQuery,
  useMarquerMessageEtudiantLuMutation,
  useRepondreMessageEtudiantMutation,
  useArchiverMessageEtudiantMutation,
  useToggleImportantMessageEtudiantMutation,
  usePushTestMutation,
  useGetMessageEtudiantThreadQuery,
  useSupprimerPostMessageEtudiantMutation,
} = messagesEtudiantsApi;
